'use strict';

import {__} from '~/shared/src/utilities/messageCatalog';
import Item from '~/shared/src/ci/Item';
import {MessageObject} from '~/shared/src/utilities/message';
import {DataStructure} from '~/shared/src/utilities/dataStructure';
import Reference from '~/shared/src/ci/Reference';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import ItemTemplateStringResolver from '~/shared/src/utilities/ItemTemplateStringResolver';
import PropertyTemplateStringResolver from '~/shared/src/utilities/PropertyTemplateStringResolver';
import ReferenceTemplateStringResolver from '~/shared/src/utilities/ReferenceTemplateStringResolver';
import ReferenceSetTemplateStringResolver from '~/shared/src/utilities/ReferenceSetTemplateStringResolver';
import ReferencePropertyTemplateStringResolver from '~/shared/src/utilities/ReferencePropertyTemplateStringResolver';
import dataUtilities, {ResolvedDataObjectChain} from '~/shared/src/utilities/dataUtils';
import UserTemplateStringResolver from '~/shared/src/utilities/UserTemplateStringResolver';
import User from '~/shared/src/model/User';

///////////////////////////
import {keys} from 'ts-transformer-keys';

import {store} from '~/frontend/src/store';
import {
    htmlQuoteEncode,
    getIconStringSpan
} from '~/frontend/src/utilities/renderHelpers';
//////////


const nodeJsGlobalKeys   = keys<NodeJS.Global>() as string[],
      unsetGlobals       = ( ( [] as string[] )
          .concat( nodeJsGlobalKeys )
          .concat( Object.keys( global ) )
          .reduce(
              ( reducedArray, gP ) => {
                  if ( gP !== 'global' && String( Number( gP ) ) !== gP && !Object.keys( reducedArray ).includes( gP ) ) {
                      reducedArray[ gP ] = undefined;
                  }

                  return reducedArray;
              },
              {} as { [ parameter : string ] : any }
          ) ),
      unsetGlobalsKeys   = Object.keys( unsetGlobals ),
      unsetGlobalsValues = Object.values( unsetGlobals );

type ResolverDataObjectParameters = {
    [ label : string ] : {
        item : ItemTemplateStringResolver;
        property : PropertyTemplateStringResolver;
        reference? : ReferenceTemplateStringResolver;
        referenceProperty? : ReferencePropertyTemplateStringResolver;
    }
};

type FunctionParameters = {
    [ name : string ] : any;
};


const messageUtilities = {

    getMessageTemplateStringResolver( item : Item, reference? : Reference | ReferenceSet, additionalResolverParameters? : {
        [ name : string ] : any
    } ) : ( templateString : string, rawMode? : boolean ) => string {
        const resolverParameters : ResolverDataObjectParameters = {
            messageBaseItem: {
                item    : new ItemTemplateStringResolver( item ),
                property: new PropertyTemplateStringResolver( item )
            }
        };

        let resolverFunctionBody : string[] = [
            'messageBaseItem.item.rawMode = rawMode;',
            'messageBaseItem.property.rawMode = rawMode;'
        ];


        if ( reference ) {
            if ( reference instanceof Reference ) {
                resolverParameters.messageBaseItem.reference = new ReferenceTemplateStringResolver( reference );
                resolverParameters.messageBaseItem.referenceProperty = new ReferencePropertyTemplateStringResolver( reference );
                resolverFunctionBody = resolverFunctionBody.concat( [
                    'messageBaseItem.reference.rawMode = rawMode;',
                    'messageBaseItem.referenceProperty.rawMode = rawMode;'
                ] );
            }
            else {
                resolverParameters.messageBaseItem.reference = new ReferenceSetTemplateStringResolver( reference );
                resolverFunctionBody = resolverFunctionBody.concat( [
                    'messageBaseItem.reference.rawMode = rawMode;'
                ] );
            }
        }

        return messageUtilities.buildReturnFunction(
            {
                ...additionalResolverParameters,
                ...resolverParameters
            },
            resolverFunctionBody
        );
    },

    getMessageTemplateStringResolverForResolvedDataObjects( item : Item, reference? : Reference, resolvedDataObjects : ResolvedDataObjectChain = {}, additionalResolverParameters? : {
        [ name : string ] : any
    } ) : ( templateString : string, rawMode? : boolean ) => string {
        const resolverParameters : ResolverDataObjectParameters = {
            messageBaseItem: {
                item    : new ItemTemplateStringResolver( item ),
                property: new PropertyTemplateStringResolver( item )
            }
        };

        let resolverFunctionBody : string[] = [
            'messageBaseItem.item.rawMode = rawMode;',
            'messageBaseItem.property.rawMode = rawMode;'
        ];

        if ( reference ) {
            resolverParameters.messageBaseItem.reference = new ReferenceTemplateStringResolver( reference );
            resolverParameters.messageBaseItem.referenceProperty = new ReferencePropertyTemplateStringResolver( reference );
            resolverFunctionBody = resolverFunctionBody.concat( [
                'messageBaseItem.reference.rawMode = rawMode;',
                'messageBaseItem.referenceProperty.rawMode = rawMode;'
            ] );
        }

        for ( const label of Object.keys( resolvedDataObjects ) as string[] ) {
            const resolvedDataObject = resolvedDataObjects[ label ];

            resolverParameters[ label ] = {
                item    : new ItemTemplateStringResolver( resolvedDataObject.item ),
                property: new PropertyTemplateStringResolver( resolvedDataObject.item )
            };
            resolverFunctionBody = resolverFunctionBody.concat( [
                `${label}.item.rawMode = rawMode;`,
                `${label}.property.rawMode = rawMode;`
            ] );

            if ( resolvedDataObject.reference ) {
                resolverParameters[ label ].reference = new ReferenceTemplateStringResolver( resolvedDataObject.reference );
                resolverParameters[ label ].referenceProperty = new ReferencePropertyTemplateStringResolver( resolvedDataObject.reference );
                resolverFunctionBody = resolverFunctionBody.concat( [
                    `${label}.reference.rawMode = rawMode;`,
                    `${label}.referenceProperty.rawMode = rawMode;`
                ] );
            }
        }

        return messageUtilities.buildReturnFunction(
            {
                ...additionalResolverParameters,
                ...resolverParameters
            },
            resolverFunctionBody
        );
    },

    getMessageTemplateStringResolverForData( data : { [ p : string ] : any }, additionalResolverParameters? : {
        [ name : string ] : any
    } ) : ( templateString : string ) => string {
        return messageUtilities.buildReturnFunction( {
            ...additionalResolverParameters,
            ...data
        } );
    },

    resolveMessageForDataStructure( message : string | MessageObject, dataStructure : DataStructure, dataContext : string, rootItem : Item, rootReference? : Reference, messagePartConcatenator : string = ', ', additionalResolverParameters? : {
        [ name : string ] : any
    } ) : string {
        let retVal : string = '';

        if ( typeof message === 'string' ) {
            retVal = messageUtilities.getMessageTemplateStringResolver( rootItem, undefined, additionalResolverParameters )( __( message ) );
        }
        else {
            const resolvedDataObjectChains = dataUtilities.getResolvedDataObjectChains( message.dataObjects, dataStructure, dataContext, rootItem.id ),
                  messageParts : string[]  = [];

            for ( const chain of resolvedDataObjectChains ) {
                messageParts.push( messageUtilities.getMessageTemplateStringResolverForResolvedDataObjects( rootItem, rootReference, chain, additionalResolverParameters )( __( message.key ) ) );
            }

            if ( messageParts.length ) {
                retVal = messageParts.join( messagePartConcatenator );
            }
            else {
                retVal = __( message.key );
            }
        }

        return retVal;
    },

    resolveMessageForQueryData( message : string, data : { [ p : string ] : any } | {
        [ p : string ] : any
    }[], messagePartConcatenator : string = ', ', additionalResolverParameters? : { [ name : string ] : any } ) : string {
        const rowData                 = Array.isArray( data ) ? data : [ data ],
              messageParts : string[] = [];

        let retVal : string = __( message );

        for ( const row of rowData ) {
            const messagePart = messageUtilities.getMessageTemplateStringResolverForData( row, additionalResolverParameters )( __( message ) );

            if ( !messageParts.includes( messagePart ) ) {
                messageParts.push( messagePart );
            }
        }

        if ( messageParts.length ) {
            retVal = messageParts.join( messagePartConcatenator );
        }

        return retVal;
    },

    buildReturnFunction( resolverParameters : FunctionParameters, resolverFunctionBody : string | string[] = '' ) : ( templateString : string, rawMode? : boolean ) => string {
        let user : User | false | undefined;

///////////////////////////////////
        user = store.getState().session.user;
/////////////////
///////////////////////////
//////////////////

        const globalParameters         = {
                  document: {},
                  global  : {},
                  user    : new UserTemplateStringResolver( user ? user : undefined ),
                  window  : {}
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  , __
/////////////////////////////////////////////
                  , htmlQuoteEncode
                  , getIconStringSpan
////////////////////////////
              },
              forbiddenWords           = [ 'abstract', 'arguments', 'await', 'boolean', 'break', 'byte', 'case', 'catch', 'char', 'class', 'const', 'continue', 'debugger', 'default', 'delete', 'do', 'double', 'else', 'enum', 'eval', 'export', 'extends', 'false', 'final', 'finally', 'float', 'for', 'function', 'goto', 'if', 'implements', 'import', 'in', 'instanceof', 'int', 'interface', 'let', 'long', 'native', 'new', 'null', 'package', 'private', 'protected', 'public', 'return', 'short', 'static', 'super', 'switch', 'synchronized', 'this', 'throw', 'throws', 'transient', 'true', 'try', 'typeof', 'var', 'void', 'volatile', 'while', 'with', 'yield' ],
              // extract only resolver parameters which are not a forbidden word and are valid variable name
              resolverParametersSave   = ( Object.keys( resolverParameters ) as string [] ).reduce(
                  ( innerRetVal, key ) => {
                      if ( !forbiddenWords.includes( key ) && key.match( /^[a-zA-Z_$][\w$]+$/ ) ) {
                          innerRetVal[ key ] = resolverParameters[ key ];
                      }
                      return innerRetVal;
                  },
                  {} as { [ key : string ] : any }
              ),
              // all the resolver parameters left
              resolverParametersUnSave = ( Object.keys( resolverParameters ) as string [] ).reduce(
                  ( innerRetVal, key ) => {
                      if ( forbiddenWords.includes( key ) || !key.match( /^[a-zA-Z_$][\w$]+$/ ) ) {
                          innerRetVal[ key ] = resolverParameters[ key ];
                      }
                      return innerRetVal;
                  },
                  {} as { [ key : string ] : any }
              ),
              resolverParameterKeys    = ( [] as string[] ).concat( unsetGlobalsKeys as string[] ).concat( Object.keys( globalParameters ) as string[] ).concat( ( Object.keys( resolverParametersSave ) as string[] ) ).concat( 'resolverParametersUnSave' ),
              resolverParameterValues  = ( [] as any[] ).concat( unsetGlobalsValues ).concat( Object.values( globalParameters ) ).concat( Object.values( resolverParametersSave ) ).concat( resolverParametersUnSave );

        if ( Array.isArray( resolverFunctionBody ) ) {
            resolverFunctionBody = resolverFunctionBody.join( ' ' );
        }

        return ( templateString : string, rawMode : boolean = false ) : string => {
            let retVal = templateString;

            templateString = templateString.replaceAll( /(\$\{|[^.])(item|property|reference|referenceProperty)\./g, '$1messageBaseItem.$2.' );

            try {
                retVal = new Function(
                    'rawMode',
                    ...resolverParameterKeys,
                    //
                    resolverFunctionBody +
                    `with ( ${resolverParameterKeys.join( ', ' )} ) { ` +
                    '    var rString = `' + templateString + '`;' +
                    '    var placeholder;' +
                    '    var match;' +
                    '    if ( resolverParametersUnSave && Object.keys( resolverParametersUnSave ).length ) {' +
                    '        for ( placeholder of Object.keys( resolverParametersUnSave ) ) {' +
                    `            rString.replaceAll( \`\\$\\{\${placeholder}\\}\`, resolverParametersUnSave[ placeholder ] );` +
                    '        }' +
                    '    }' +
                    '    while ( match = rString.match( /(?:\\$\\{)?__\\((?:\\s(?:\'|")([^\\)]*)(?:\'|")\\s)\\)(?:\\})?/ ) ) {' +
                    '        rString = rString.replace( match[ 0 ], __( match[ 1 ] ) );' +
                    '    }' +
                    '    return rString;' +
                    '}'
                ).bind(
                    null,
                    rawMode,
                    ...resolverParameterValues
                )();
            }
            catch ( e ) {
                //console.error( e );
            }

            retVal = retVal.replaceAll( /(\$\{|[^.])messageBaseItem\./g, '$1' );

            return retVal;
        }
    }
};

export default messageUtilities;
